import type { ReactNode } from 'react';
import { ChevronRight, Target } from 'lucide-react';

import type { SheetLead } from '../../../hooks/useLeadSheet';
import { leadAddress, leadContactName, leadErp } from '../../../lib/crm/leadSheet';
import { BLOCK_ROW, REF } from '../../bureau/encre';
import { Pastille, SheetBlock, SheetEmpty } from './EncreSheet';

function Fields({ fields, empty }: { fields: [string, ReactNode][]; empty: string }) {
  const known = fields.filter(([, value]) => value);
  if (known.length === 0) return <SheetEmpty>{empty}</SheetEmpty>;
  return (
    <dl className="grid grid-cols-[88px_minmax(0,1fr)] gap-x-4 gap-y-1.5 text-sm">
      {known.map(([label, value]) => (
        <div key={label} className="contents">
          <dt className="text-desk-muted">{label}</dt>
          <dd className="m-0 min-w-0 break-words text-desk-strong">{value}</dd>
        </div>
      ))}
    </dl>
  );
}

/** « Entreprise » and « Contact »: who the client is and who to reach, each way a link. */
export function LeadIdentityBlocks({ lead }: { lead: SheetLead }) {
  const row = lead.row;
  const email = row.contact_email?.trim();
  const phone = row.contact_phone?.trim();
  return (
    <>
      <SheetBlock title="Entreprise">
        <Fields
          empty="Rien de renseigné : « Modifier » permet de compléter."
          fields={[
            ['SIRET', row.siret?.trim() || null],
            ['Adresse', leadAddress(row)],
            ['ERP', leadErp(row)],
          ]}
        />
      </SheetBlock>
      <SheetBlock title="Contact">
        <Fields
          empty="Aucun contact renseigné."
          fields={[
            ['Nom', leadContactName(row)],
            [
              'Mail',
              email ? (
                <a href={`mailto:${email}`} className={REF}>
                  {email}
                </a>
              ) : null,
            ],
            [
              'Téléphone',
              phone ? (
                <a href={`tel:${phone}`} className={REF}>
                  {phone}
                </a>
              ) : null,
            ],
          ]}
        />
      </SheetBlock>
    </>
  );
}

/** « Opportunité »: the deal the lead comes from, opening its sheet. */
export function LeadDealBlock({ lead, onOpen }: { lead: SheetLead; onOpen: (id: string) => void }) {
  const deal = lead.opportunity;
  if (!deal) return null;
  return (
    <SheetBlock title="Opportunité">
      <button type="button" onClick={() => onOpen(deal.id)} className={BLOCK_ROW}>
        <Pastille icon={Target} />
        <span className="min-w-0 flex-1 truncate text-sm font-medium text-desk-ink">{deal.name}</span>
        <ChevronRight size={16} className="shrink-0 text-desk-faint" aria-hidden="true" />
      </button>
    </SheetBlock>
  );
}

/** « Notes internes »: what the team noted on the lead (edited with « Modifier »). */
export function LeadInternalNotes({ lead }: { lead: SheetLead }) {
  const notes = lead.row.internal_notes?.trim();
  if (!notes) return null;
  return (
    <SheetBlock title="Notes internes">
      <p className="whitespace-pre-wrap text-sm text-desk-strong" data-sensitive-text="">{notes}</p>
    </SheetBlock>
  );
}
